import { ListStore, StoreState } from '@deqo/foundation';
import { Bezier } from 'bezier-js';
import { injectable } from 'inversify';
import { Matrix } from '../../../platform/geom/Matrix';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { Physical } from '../../canvas/entities/Physical';
import { HandControl } from './HandControl';
import { HandLayout } from './HandLayout';

export interface HoldLayoutItem {
    readonly id: string;
    readonly transform: Matrix;
    readonly transition: string;
    readonly holdOutline: Polyline;
    readonly lookOutline?: Polyline;
    readonly order: number;
}

export type HandLayoutParams = StoreState<HandControl> & StoreState<HandLayout> & {
    items: Physical[],
};

const fitScale = (size: Point) => {
    const mw = 140;
    const mh = 180;
    return Math.max(Math.min(mw / size.w, 1), Math.min(mh / size.h, 1));
};

const fit = (size: Point) => size.multiply(fitScale(size));

const computePlots = (dash: Bezier, items: Physical[]) => {

    // Compute the position of each object along the dash as T along the bezier.  We calculate their combined width then scale it down using
    // the available space which is basically a right to left resize operation.  The position of each item depends on the last so we have to
    // loop with a rolling aggregate.  The actual plot position is the center point of the fitted size.

    const avail = dash.length();
    const total = items.reduce((t, x) => t + fit(x.size()).w, 0);
    const scale = avail / total;

    let aggr = 0;
    const plots = new Map<Physical, number>();

    for (const tm of items) {
        const fs = fit(tm.size());
        plots.set(tm, ((aggr + (fs.w / 2)) * scale) / avail);
        aggr += fs.w;
    }

    return plots;
};

@injectable()
export class HoldLayout extends ListStore<HoldLayoutItem> {

    public update({ items, frame, dash, target, drag }: HandLayoutParams) {

        dash = dash.split(.1, .9);

        // DebugOverlay.point('s', Point.from(dash.get(0)), 'blue', 10);
        // DebugOverlay.point('e', Point.from(dash.get(1)), 'blue', 10);

        const plots = computePlots(dash, items);
        const update = items.map((x, i) => {

            const id = x.id;
            const mt = Point.from(dash.get(plots.get(x)));
            const nm = Point.from(dash.derivative(plots.get(x))).normalize();

            const size = x.size();
            const fs = fitScale(size);

            const hold = Matrix.identity
                .translate(mt)
                .rotate(nm.angle())
                .translate(0, 30)
                .scale(fs)
                .translate(size.divide(-2))
                ;

            // Targets go at the end, others go in order
            // const hold = computeHoldTransform(frame, ns, i, items.length).translate(ns.w / -2, ns.h / -2);
            const holdOutline = Polyline.from(hold.apply(new Rect(0, 0, size.w, size.h)), true);

            const order = x === target ? items.length + 1 : i;

            const props = { id, transform: hold, order, holdOutline, transition: 'slow' };

            if (x === target) {
                // Special transform for look which is affected by the drag control
                const offset = !!drag ? Point.vector(drag[0], drag[1]) : Point.zero;
                const look = Matrix.identity
                    .translate(mt)
                    .translate(nm.perp().multiply(size.h * .33))
                    .translate(size.divide(-2))
                    .translate(offset)
                    ;
                const lookOutline = Polyline.from(look.apply(new Rect(0, 0, size.w, size.h)), true);
                return { ...props, transform: look, lookOutline, transition: 'fast' };
            }

            return props;
        });

        //     m.splice(0, m.length, ...update);

        // });

        this.updateState(x => x.splice(0, x.length, ...update));

        // DebugOverlay.mode = 'view';
        // // const haaa = new Rect((frame.w / 2) - 100, frame.h - 100, 200, 100);
        // const haaa = new Rect((frame.w / 2) - 240, frame.h - 100, 480, 100);


    }
}
